import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";

const log = createFileLogger('USER_INTERACTION');

export const userConfirmCommand: SelfContainedCommand = {
  id: 'user_confirm',
  description: `Ask user to confirm an action before proceeding.

This command presents a confirmation dialog with clear details about what will happen,
and waits for the user to explicitly confirm or cancel. Use this for:

**Critical Actions:**
• Making payments
• Submitting forms with legal/financial impact
• Deleting important data
• Authorizing access to accounts

**Benefits:**
• User always aware of important actions
• Prevents accidental operations
• Provides audit trail of confirmations
• Shows structured details for review`,
  autonomyLevel: 'ask',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'message',
        type: 'string',
        description: 'Confirmation message/question (e.g., "Pay $312 for F-250 registration?")',
        required: true
      },
      {
        name: 'details',
        type: 'object',
        description: 'Structured details to display (e.g., {vehicle: "Ford F-250", amount: "$312", dueDate: "2026-01-15"})',
        required: false
      },
      {
        name: 'confirmButtonText',
        type: 'string',
        description: 'Text for confirm button (defaults to "Confirm")',
        required: false
      },
      {
        name: 'cancelButtonText',
        type: 'string',
        description: 'Text for cancel button (defaults to "Cancel")',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { 
          message: 'Pay $312 for Ford F-250 registration?',
          details: {
            vehicle: 'Ford F-250',
            plate: '7ABC123',
            state: 'CA',
            amount: '$312',
            dueDate: '2026-01-15'
          }
        },
        expectedOutput: createSuccessResult('User confirmed', {
          confirmed: true
        }),
        description: 'Confirm payment',
        context: 'Before submitting payment'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 5000,
    complexity: 'low'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { message, details, confirmButtonText, cancelButtonText } = params;
    
    try {
      log.info('[user_confirm] Requesting confirmation:', { message, details });
      
      if (!message) {
        return createErrorResult(
          'Missing message',
          'message parameter is required',
          'validation',
          { data: params }
        );
      }
      
      return createSuccessResult(
        'User confirmed',
        {
          confirmed: true,
          message,
          details,
          confirmButtonText: confirmButtonText || 'Confirm',
          cancelButtonText: cancelButtonText || 'Cancel',
          timestamp: new Date().toISOString()
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            requiresUserInteraction: true
          } as any
        }
      );
      
    } catch (error: any) {
      log.error('[user_confirm] Error:', error);
      return createErrorResult(
        'Confirmation failed',
        `Failed to get user confirmation: ${error.message || 'Unknown error'}`,
        'execution',
        {
          data: { message, details },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};
